import { useState } from "react";

import {
  FnDesc,
  PlaygroundAgentConfig,
  PlaygroundComponentConfig,
  PlaygroundComponentMessages,
  PlaygroundSeed,
  PlaygroundSeedMessage,
  PlaygroundSource,
} from "../types";
import {
  PlaygroundStep,
  PlaygroundStepStatuses,
  UNTOUCHED_STEP_STATUSES,
} from "./PlaygroundStepper";

export type PlaygroundDraft = {
  // null for a from-scratch draft: no moment behind it.
  source: PlaygroundSource | null;
  seed: PlaygroundSeed;
  stepStatuses: PlaygroundStepStatuses;
};

// The service entry agent_llm names — the one service the Playground edits.
export const agentLlmService = (agentConfig: PlaygroundAgentConfig) =>
  (agentConfig.service_configs ?? []).find(
    (service) =>
      typeof service.id === "string" && service.id === agentConfig.agent_llm
  );

// The org-service name that entry carries, when it names one.
export const agentLlmServiceName = (
  agentConfig: PlaygroundAgentConfig
): string | null => {
  const entry = agentLlmService(agentConfig);
  return typeof entry?.service_name === "string" ? entry.service_name : null;
};

// The params riding that entry: everything but its identity fields.
export const agentLlmServiceParams = (
  agentConfig: PlaygroundAgentConfig
): Record<string, unknown> => {
  const entry = agentLlmService(agentConfig);
  if (!entry) {
    return {};
  }
  const params: Record<string, unknown> = { ...entry };
  delete params.id;
  delete params.service_name;
  return params;
};

export const isSameSource = (
  a: PlaygroundSource | null,
  b: PlaygroundSource | null
): boolean =>
  !!a &&
  !!b &&
  a.sessionId === b.sessionId &&
  a.agentId === b.agentId &&
  a.momentId === b.momentId;

const DISCARD_WARNING =
  "You have unsaved Playground edits. Loading a different source will discard them.";

// Remove a component's messages from the componentMessages map
const withoutComponent = (
  componentMessages: PlaygroundComponentMessages,
  componentId: string | undefined
): PlaygroundComponentMessages => {
  if (componentId === undefined || !(componentId in componentMessages)) {
    return componentMessages;
  }
  const rest = { ...componentMessages };
  delete rest[componentId];
  return rest;
};

// Propagate a change to a component through the playground draft,
// and return an updated draft.
const withComponentCascade = (
  previous: PlaygroundDraft,
  agentConfig: PlaygroundAgentConfig,
  componentMessages: PlaygroundComponentMessages
): PlaygroundDraft => {
  const stepStatuses: PlaygroundStepStatuses = {
    ...previous.stepStatuses,
    [PlaygroundStep.AgentConfig]: "edited",
  };
  if (componentMessages !== previous.seed.component_messages) {
    stepStatuses[PlaygroundStep.AgentState] = "edited";
  }
  return {
    ...previous,
    seed: {
      ...previous.seed,
      agent_config: agentConfig,
      component_messages: componentMessages,
    },
    stepStatuses,
  };
};

export const usePlaygroundDraft = (): {
  draft: PlaygroundDraft | null;
  isDirty: boolean;
  seedDraft: (source: PlaygroundSource | null, seed: PlaygroundSeed) => void;
  updateComponentMessages: (
    componentId: string,
    messages: Array<PlaygroundSeedMessage>
  ) => void;
  updateAgentConfig: (agentConfig: PlaygroundAgentConfig) => void;
  deleteComponent: (component: PlaygroundComponentConfig) => void;
  renameComponent: (
    component: PlaygroundComponentConfig,
    nextId: string
  ) => void;
  updateFunctions: (functions: Array<FnDesc>) => void;
  confirmDiscard: () => boolean;
} => {
  const [draft, setDraft] = useState<PlaygroundDraft | null>(null);

  const isDirty =
    !!draft &&
    Object.values(draft.stepStatuses).some((status) => status === "edited");

  const seedDraft = (source: PlaygroundSource | null, seed: PlaygroundSeed) => {
    setDraft({
      source,
      seed,
      stepStatuses: { ...UNTOUCHED_STEP_STATUSES },
    });
  };

  // The Agent State editors' apply path: replace one component's messages
  // and record the step edit.
  const updateComponentMessages = (
    componentId: string,
    messages: Array<PlaygroundSeedMessage>
  ) => {
    setDraft((previous) =>
      previous
        ? {
            ...previous,
            seed: {
              ...previous.seed,
              component_messages: {
                ...previous.seed.component_messages,
                [componentId]: messages,
              },
            },
            stepStatuses: {
              ...previous.stepStatuses,
              [PlaygroundStep.AgentState]: "edited",
            },
          }
        : previous
    );
  };

  // The Functions editor's apply path: replace the function list and
  // record the step edit.
  const updateFunctions = (functions: Array<FnDesc>) => {
    setDraft((previous) =>
      previous
        ? {
            ...previous,
            seed: { ...previous.seed, functions },
            stepStatuses: {
              ...previous.stepStatuses,
              [PlaygroundStep.Functions]: "edited",
            },
          }
        : previous
    );
  };

  // The Agent Config editors' apply path: replace the config block and
  // record the step edit.
  const updateAgentConfig = (agentConfig: PlaygroundAgentConfig) => {
    setDraft((previous) =>
      previous
        ? {
            ...previous,
            seed: { ...previous.seed, agent_config: agentConfig },
            stepStatuses: {
              ...previous.stepStatuses,
              [PlaygroundStep.AgentConfig]: "edited",
            },
          }
        : previous
    );
  };

  const deleteComponent = (component: PlaygroundComponentConfig) => {
    setDraft((previous) => {
      if (!previous) {
        return previous;
      }
      const config = previous.seed.agent_config;
      return withComponentCascade(
        previous,
        {
          ...config,
          component_configs: (config.component_configs ?? []).filter(
            (candidate) => candidate !== component
          ),
          presentation_config: config.presentation_config && {
            ...config.presentation_config,
            presentation_order: (
              config.presentation_config.presentation_order ?? []
            ).filter(([componentId]) => componentId !== component.id),
          },
        },
        withoutComponent(previous.seed.component_messages, component.id)
      );
    });
  };

  const renameComponent = (
    component: PlaygroundComponentConfig,
    nextId: string
  ) => {
    setDraft((previous) => {
      if (!previous) {
        return previous;
      }
      const config = previous.seed.agent_config;
      const componentMessages = previous.seed.component_messages;
      const messages =
        component.id === undefined
          ? undefined
          : componentMessages[component.id];
      return withComponentCascade(
        previous,
        {
          ...config,
          component_configs: (config.component_configs ?? []).map(
            (candidate) =>
              candidate === component ? { ...candidate, id: nextId } : candidate
          ),
          presentation_config: config.presentation_config && {
            ...config.presentation_config,
            presentation_order: (
              config.presentation_config.presentation_order ?? []
            ).map(([componentId, element]): [string, string] =>
              componentId === component.id
                ? [nextId, element]
                : [componentId, element]
            ),
          },
        },
        messages
          ? {
              ...withoutComponent(componentMessages, component.id),
              [nextId]: messages,
            }
          : componentMessages
      );
    });
  };

  // Call before switching to a different source agent/session/moment; true means it is
  // safe to proceed (nothing to lose, or the user accepted the loss).
  const confirmDiscard = (): boolean =>
    !isDirty || window.confirm(DISCARD_WARNING);

  return {
    draft,
    isDirty,
    seedDraft,
    updateComponentMessages,
    updateAgentConfig,
    deleteComponent,
    renameComponent,
    updateFunctions,
    confirmDiscard,
  };
};
